import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-4" };
const _hoisted_2 = {
    key: 0,
    class: "text-sm font-semibold text-(--colors-text-default)",
    "data-testid": "tax-receipt-cancel-modal-covered-donations"
};
const _hoisted_3 = { class: "flex flex-col gap-2" };
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Button, Modal, SelectInput, TextInput } from "@eudonet/opal";
import { TAX_RECEIPT_CANCELLATION_REASON_CODES, } from "../../../domain/entities/TaxReceipt";
/** Mirrors the OPDON persisted-column bound behind TaxReceipt.CancellationReason.TooLong. */
const COMMENT_MAX_LENGTH = 1000;
export default /*@__PURE__*/ _defineComponent({
    __name: 'TaxReceiptCancelModal',
    props: {
        modelValue: { type: Boolean },
        receipt: {},
        cancelling: { type: Boolean, default: false }
    },
    emits: ["update:modelValue", "confirm"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const isOpen = computed({
            get: () => props.modelValue,
            set: (value) => {
                // While the cancel call is in flight every dismissal path (X, Escape, backdrop) is held,
                // matching the disabled secondary button.
                if (props.cancelling && !value)
                    return;
                emit("update:modelValue", value);
            },
        });
        const reasonCode = ref("");
        const comment = ref("");
        const reasonCodeError = ref(undefined);
        const commentError = ref(undefined);
        const isOtherSelected = computed(() => reasonCode.value === "Other");
        const isAnnual = computed(() => props.receipt?.mode === "AnnualGrouped");
        const coveredDonationCount = computed(() => isAnnual.value ? (props.receipt?.coveredDonationCount ?? null) : null);
        // A cancelled annual receipt can still be regenerated (OP-7612): it is not "no further action".
        const messageKey = computed(() => isAnnual.value
            ? "finance.donations.taxReceipt.cancelModal.annualMessage"
            : "finance.donations.taxReceipt.cancelModal.message");
        const reasonOptions = computed(() => TAX_RECEIPT_CANCELLATION_REASON_CODES.map((code) => ({
            value: code,
            label: t(`finance.donations.taxReceipt.cancelModal.motif.${code}`),
        })));
        // A fresh open must never inherit the previous cancellation's selection or errors.
        watch(() => props.modelValue, (open) => {
            if (open) {
                reasonCode.value = "";
                comment.value = "";
                reasonCodeError.value = undefined;
                commentError.value = undefined;
            }
        });
        function handleConfirm() {
            if (props.cancelling)
                return;
            if (!reasonCode.value) {
                reasonCodeError.value = t("finance.donations.taxReceipt.cancelModal.motifRequired");
                return;
            }
            const trimmedComment = comment.value.trim();
            if (isOtherSelected.value && !trimmedComment) {
                commentError.value = t("finance.donations.taxReceipt.cancelModal.commentRequired");
                return;
            }
            // The comment ref survives the field's v-if unmount, so a text typed under "Other" must not
            // ride along when the user switched to another motive before confirming.
            emit("confirm", reasonCode.value, isOtherSelected.value && trimmedComment ? trimmedComment : null);
        }
        return (_ctx, _cache) => {
            const _component_i18n_t = _resolveComponent("i18n-t");
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: isOpen.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((isOpen).value = $event)),
                size: "sm",
                "show-close-button": !__props.cancelling,
                "data-testid": "tax-receipt-cancel-modal"
            }, {
                header: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)("finance.donations.taxReceipt.cancelModal.title")), 1 /* TEXT */)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _unref(t)('form.cancel'),
                        disabled: __props.cancelling,
                        "data-testid": "tax-receipt-cancel-modal-dismiss",
                        onClick: _cache[4] || (_cache[4] = ($event) => (isOpen.value = false))
                    }, null, 8 /* PROPS */, ["label", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "error",
                        label: _unref(t)('finance.donations.taxReceipt.cancelModal.confirm'),
                        loading: __props.cancelling,
                        "data-testid": "tax-receipt-cancel-modal-confirm",
                        onClick: handleConfirm
                    }, null, 8 /* PROPS */, ["label", "loading"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createVNode(_component_i18n_t, {
                            keypath: messageKey.value,
                            tag: "p",
                            scope: "global",
                            class: "text-sm text-(--colors-text-default)",
                            "data-testid": "tax-receipt-cancel-modal-message"
                        }, {
                            number: _withCtx(() => [
                                _createElementVNode("strong", null, _toDisplayString(__props.receipt?.receiptNumber), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["keypath"]),
                        (coveredDonationCount.value !== null)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_unref(t)("finance.donations.taxReceipt.cancelModal.coveredDonations", { count: coveredDonationCount.value }, coveredDonationCount.value)), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true),
                        _createElementVNode("div", _hoisted_3, [
                            _createVNode(_unref(SelectInput), {
                                modelValue: reasonCode.value,
                                "onUpdate:modelValue": [
                                    _cache[0] || (_cache[0] = ($event) => ((reasonCode).value = $event)),
                                    _cache[1] || (_cache[1] = ($event) => (reasonCodeError.value = undefined))
                                ],
                                "full-width": "",
                                required: "",
                                label: _unref(t)('finance.donations.taxReceipt.cancelModal.motifLabel'),
                                placeholder: _unref(t)('finance.donations.taxReceipt.cancelModal.motifPlaceholder'),
                                options: reasonOptions.value,
                                error: reasonCodeError.value,
                                "data-testid": "tax-receipt-cancel-modal-motif"
                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options", "error"]),
                            (isOtherSelected.value)
                                ? (_openBlock(), _createBlock(_unref(TextInput), {
                                    key: 0,
                                    modelValue: comment.value,
                                    "onUpdate:modelValue": [
                                        _cache[2] || (_cache[2] = ($event) => ((comment).value = $event)),
                                        _cache[3] || (_cache[3] = ($event) => (commentError.value = undefined))
                                    ],
                                    "full-width": "",
                                    required: "",
                                    label: _unref(t)('finance.donations.taxReceipt.cancelModal.commentLabel'),
                                    placeholder: _unref(t)('finance.donations.taxReceipt.cancelModal.commentPlaceholder'),
                                    maxlength: COMMENT_MAX_LENGTH,
                                    error: commentError.value,
                                    "data-testid": "tax-receipt-cancel-modal-comment"
                                }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]))
                                : _createCommentVNode("v-if", true)
                        ])
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "show-close-button"]));
        };
    }
});
